/**
 * CSS overlay layers for cinematic filter preview.
 * Uses normal alpha compositing (no mix-blend-mode) so layers work on top of <video>.
 */

export const OVERLAY_FILTER_IDS = new Set([
  'glossy',
  'sunlight',
  'vignette',
  'sepia',
  'light_leak',
]);

function normalizeOverlayFilterId(id) {
  const key = String(id || '').trim().toLowerCase().replace(/-/g, '_');
  return OVERLAY_FILTER_IDS.has(key) ? key : null;
}

export function filterUsesOverlayPreview(id) {
  return OVERLAY_FILTER_IDS.has(String(id || '').trim().toLowerCase().replace(/-/g, '_'));
}

function t01(intensity) {
  const n = Number(intensity);
  if (!Number.isFinite(n)) return 0.5;
  return Math.max(0, Math.min(1, n / 100));
}

/** @returns {Array<{ id: string, style: object }>} */
export function buildFilterOverlayLayers(filters) {
  const layers = [];
  for (const raw of filters || []) {
    const id = normalizeOverlayFilterId(raw?.id ?? raw?.filter_id);
    if (!id) continue;
    const t = t01(raw?.intensity);

    if (id === 'glossy') {
      layers.push({
        id: 'glossy-sheen',
        style: {
          background: `linear-gradient(
            125deg,
            rgba(255,255,255,${(0.55 * t).toFixed(3)}) 0%,
            rgba(255,255,255,${(0.18 * t).toFixed(3)}) 32%,
            rgba(255,255,255,0) 58%
          )`,
        },
      });
      layers.push({
        id: 'glossy-spark',
        style: {
          background: `radial-gradient(
            ellipse 50% 35% at 86% 14%,
            rgba(255,255,255,${(0.72 * t).toFixed(3)}) 0%,
            rgba(255,255,255,0) 72%
          )`,
        },
      });
    }

    if (id === 'sunlight') {
      layers.push({
        id: 'sunlight-glow',
        style: {
          background: `radial-gradient(
            ellipse 75% 60% at 84% 6%,
            rgba(255,210,90,${(0.72 * t).toFixed(3)}) 0%,
            rgba(255,170,60,${(0.38 * t).toFixed(3)}) 38%,
            rgba(255,130,30,0) 75%
          )`,
        },
      });
      layers.push({
        id: 'sunlight-rays',
        style: {
          background: `linear-gradient(
            210deg,
            rgba(255,230,150,${(0.45 * t).toFixed(3)}) 0%,
            rgba(255,190,90,${(0.15 * t).toFixed(3)}) 22%,
            rgba(255,160,50,0) 48%
          )`,
        },
      });
    }

    if (id === 'vignette') {
      layers.push({
        id: 'vignette-ring',
        style: {
          background: `radial-gradient(
            ellipse 88% 78% at 50% 50%,
            rgba(0,0,0,0) 38%,
            rgba(0,0,0,${(0.75 * t).toFixed(3)}) 100%
          )`,
        },
      });
    }

    if (id === 'sepia') {
      layers.push({
        id: 'sepia-tint',
        style: {
          background: `rgba(95,55,18,${(0.42 * t).toFixed(3)})`,
        },
      });
    }

    if (id === 'light_leak') {
      layers.push({
        id: 'leak-left',
        style: {
          background: `linear-gradient(90deg, rgba(255,140,60,${(0.62 * t).toFixed(3)}) 0%, rgba(255,100,40,0) 42%)`,
        },
      });
      layers.push({
        id: 'leak-right',
        style: {
          background: `linear-gradient(270deg, rgba(255,190,100,${(0.45 * t).toFixed(3)}) 0%, rgba(255,160,70,0) 32%)`,
        },
      });
    }
  }
  return layers;
}

/** CSS filter for theme/special filters + grade under overlays. */
export function cssPreviewForNonOverlayFilters(filters) {
  const parts = [];
  for (const raw of filters || []) {
    const id = String((raw?.id ?? raw?.filter_id) || '').trim().toLowerCase().replace(/-/g, '_');
    if (!id || OVERLAY_FILTER_IDS.has(id)) continue;
    const t = t01(raw?.intensity);
    switch (id) {
      case 'black_white':
        parts.push(`grayscale(${t})`);
        break;
      case 'warmth':
        parts.push(`sepia(${t * 0.55})`, `saturate(${1 + t * 0.45})`, `brightness(${1 + t * 0.08})`);
        break;
      case 'cool_tone':
        parts.push(`hue-rotate(${-28 * t}deg)`, `saturate(${1 + t * 0.3})`, `brightness(${1 - t * 0.04})`);
        break;
      case 'vibrance':
        parts.push(`saturate(${1 + t * 1.35})`);
        break;
      case 'contrast':
        parts.push(`contrast(${1 + t * 1.1})`);
        break;
      case 'glow':
        parts.push(`brightness(${1 + t * 0.28})`, `blur(${t * 3}px)`);
        break;
      case 'film_grain':
        parts.push(`contrast(${1 + t * 0.22})`, `saturate(${1 - t * 0.1})`);
        break;
      case 'lens_flare':
        parts.push(`brightness(${1 + t * 0.22})`, `saturate(${1 + t * 0.15})`);
        break;
      default:
        break;
    }
  }
  for (const raw of filters || []) {
    const id = normalizeOverlayFilterId(raw?.id ?? raw?.filter_id);
    const t = t01(raw?.intensity);
    if (id === 'glossy') {
      parts.push(`contrast(${1 + t * 0.18})`, `saturate(${1 + t * 0.12})`, `brightness(${1 + t * 0.06})`);
    }
    if (id === 'sunlight') {
      parts.push(`brightness(${1 + t * 0.18})`, `saturate(${1 + t * 0.22})`);
    }
    if (id === 'sepia') {
      parts.push(`sepia(${t * 0.95})`, `contrast(${1 + t * 0.1})`);
    }
    if (id === 'light_leak') {
      parts.push(`brightness(${1 + t * 0.12})`, `saturate(${1 + t * 0.18})`);
    }
  }
  return parts.length ? parts.join(' ') : undefined;
}
